import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button, Card, Badge, Spinner, Empty, fmtBRL, fmtKg, fmtNum } from "@/components/ui/Primitives";
import { api, formatErr, fmtDate } from "@/lib/api";
import { DollarSign, ChevronRight, CheckCircle2, Package, Scale, BarChart3, Percent } from "lucide-react";

const CATS = [
  { k: "cx1",  label: "CX1",  subtitle: "Refugo",  color: "bg-red-100 text-red-800" },
  { k: "cx2",  label: "CX2",  subtitle: "Pequena", color: "bg-orange-100 text-orange-800" },
  { k: "cx3",  label: "CX3",  subtitle: "Média",   color: "bg-emerald-100 text-emerald-800" },
  { k: "cx3g", label: "CX3G", subtitle: "Grande",  color: "bg-sky-100 text-sky-800" },
  { k: "cx4",  label: "CX4",  subtitle: "Extra",   color: "bg-purple-100 text-purple-800" },
];

export default function BenefPrecos() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(null);
  const [searchParams] = useSearchParams();
  const nfQuery = searchParams.get("nf");

  const load = async (keepSelectedId) => {
    setLoading(true);
    try {
      const { data } = await api.get("/invoices", { params: { status: "beneficiada", page: 1, page_size: 200 } });
      setItems(data.items || []);
      const targetId = keepSelectedId || nfQuery;
      if (targetId) {
        const found = (data.items || []).find((i) => i.id === targetId);
        if (found) setSelected(found);
      }
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const pendentes = items.filter((i) => !(i.valor_total_caixas > 0));
  const definidos = items.filter((i) => i.valor_total_caixas > 0);

  return (
    <Layout
      title="Preços & Custos"
      subtitle="Definir preços por categoria e custos das NFs beneficiadas"
    >
      {err && <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md mb-4">{err}</div>}

      {/* Cards resumo */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-5">
        <Card className="p-4">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Aguardando preços</div>
          <div className="font-mono text-2xl font-semibold text-danger mt-1">{pendentes.length}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Concluídas</div>
          <div className="font-mono text-2xl font-semibold text-emerald-700 mt-1">{definidos.length}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Total NFs beneficiadas</div>
          <div className="font-mono text-2xl font-semibold text-ink-900 mt-1">{items.length}</div>
        </Card>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Spinner size={28} /></div>
      ) : items.length === 0 ? (
        <Card><Empty title="Nenhuma NF beneficiada" hint="Finalize um beneficiamento para poder definir os preços." /></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Lista à esquerda */}
          <div className="lg:col-span-1 space-y-3">
            <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-2">
              {pendentes.length > 0 ? `Aguardando (${pendentes.length})` : "Todas concluídas"}
            </div>
            {pendentes.map((i) => (
              <button
                key={i.id}
                onClick={() => setSelected(i)}
                className={`w-full text-left p-3 rounded-lg border transition-all ${selected?.id === i.id ? "bg-emerald-50 border-brand-500 shadow-sm" : "bg-white border-ink-200 hover:border-brand-500"}`}
                data-testid={`nf-item-${i.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-mono font-semibold">#{i.nota_numero}</div>
                  <Badge tone="danger">Sem preço</Badge>
                </div>
                <div className="text-sm text-ink-700 mt-1 truncate">{i.fornecedor}</div>
                <div className="text-xs text-ink-500 font-mono mt-1">
                  {fmtDate(i.data_beneficiamento)} • {fmtNum(i.total_caixas, 0)} cx
                </div>
              </button>
            ))}
            {definidos.length > 0 && (
              <>
                <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mt-4 mb-2">Definidos</div>
                {definidos.slice(0, 8).map((i) => (
                  <button
                    key={i.id}
                    onClick={() => setSelected(i)}
                    className={`w-full text-left p-3 rounded-lg border transition-all ${selected?.id === i.id ? "bg-emerald-50 border-brand-500 shadow-sm" : "bg-stone-50 border-ink-200 hover:border-brand-500"}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-mono font-semibold">#{i.nota_numero}</div>
                      <CheckCircle2 size={16} className="text-emerald-600" />
                    </div>
                    <div className="text-sm text-ink-700 mt-1 truncate">{i.fornecedor}</div>
                    <div className="text-xs text-ink-500 font-mono mt-1">{fmtBRL(i.valor_liquido_fornecedor)}</div>
                  </button>
                ))}
              </>
            )}
          </div>

          {/* Formulário à direita */}
          <div className="lg:col-span-2">
            {selected ? (
              <PriceForm key={selected.id} nf={selected} onSaved={async () => { await load(selected.id); }} />
            ) : (
              <Card><Empty title="Selecione uma NF" hint="Escolha uma nota fiscal à esquerda para definir os preços." /></Card>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}

function PriceForm({ nf, onSaved }) {
  const nav = useNavigate();
  const benef = nf.beneficiamento || {};
  const custos = benef.custos || {};
  const [form, setForm] = useState({
    modo_precificacao: benef.modo_precificacao || "boca_maquina",
    preco_balancao: benef.preco_balancao ?? 0,
    desconto_balancao_pct: benef.desconto_balancao_pct ?? 0,
    cx1: benef.cx1?.preco ?? 0,
    cx2: benef.cx2?.preco ?? 0,
    cx3: benef.cx3?.preco ?? 0,
    cx3g: benef.cx3g?.preco ?? 0,
    cx4: benef.cx4?.preco ?? 0,
    chapa: custos.chapa ?? 0,
    icms_seguro: custos.icms_seguro ?? 0,
    frete: custos.frete ?? 0,
    fun_rural_pct: custos.fun_rural_pct ?? 1.5,
    custo_maquinacao: custos.custo_maquinacao ?? 0,
    sacaria: custos.sacaria ?? 0,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [preview, setPreview] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setMode = (m) => setForm({ ...form, modo_precificacao: m });

  useEffect(() => { setForm({
    modo_precificacao: benef.modo_precificacao || "boca_maquina",
    preco_balancao: benef.preco_balancao ?? 0,
    desconto_balancao_pct: benef.desconto_balancao_pct ?? 0,
    cx1: benef.cx1?.preco ?? 0, cx2: benef.cx2?.preco ?? 0, cx3: benef.cx3?.preco ?? 0,
    cx3g: benef.cx3g?.preco ?? 0, cx4: benef.cx4?.preco ?? 0,
    chapa: custos.chapa ?? 0, icms_seguro: custos.icms_seguro ?? 0, frete: custos.frete ?? 0,
    fun_rural_pct: custos.fun_rural_pct ?? 1.5,
    custo_maquinacao: custos.custo_maquinacao ?? 0,
    sacaria: custos.sacaria ?? 0,
  }); setPreview(null); /* eslint-disable-next-line */ }, [nf]);

  // ---- Cálculos + informações do beneficiamento ----
  const totalCaixas = Number(nf.total_caixas) || 0;
  const pesoLiquido = Number(nf.peso_liquido) || 0;
  const quebraKg = Math.max(0, Number(nf.quebra_kg) || 0);
  const quebraPct = pesoLiquido > 0 ? (quebraKg / pesoLiquido) * 100 : 0;

  const catsInfo = CATS.map((c) => {
    const q = Number(benef[c.k]?.quantidade || 0);
    const pct = totalCaixas > 0 ? (q / totalCaixas) * 100 : 0;
    return { ...c, quantidade: q, pct };
  });

  const calc = () => {
    let valorBruto;
    let pesoPagavel = pesoLiquido;
    if (form.modo_precificacao === "balancao") {
      const descPct = Math.max(0, Number(form.desconto_balancao_pct) || 0);
      pesoPagavel = pesoLiquido * (1 - descPct / 100);
      valorBruto = pesoPagavel * (Number(form.preco_balancao) || 0);
    } else {
      valorBruto = 0;
      for (const c of CATS) {
        const q = Number(benef[c.k]?.quantidade || 0);
        const p = Number(form[c.k] || 0);
        valorBruto += q * p;
      }
    }
    const funrural = valorBruto * (Number(form.fun_rural_pct) || 0) / 100;
    const chapa = Number(form.chapa) || 0;
    const icms = Number(form.icms_seguro) || 0;
    // Frete: R$ por tonelada líquida × peso líquido em toneladas (antes do beneficiamento)
    const pesoLiqNF = Number(nf.peso_liquido) || 0;
    const tonLiq = pesoLiqNF / 1000;
    const fretePorTon = Number(form.frete) || 0;
    const frete = fretePorTon * tonLiq;
    const custoMaq = (Number(form.custo_maquinacao) || 0) * totalCaixas;
    const custoSac = (Number(form.sacaria) || 0) * totalCaixas;
    return {
      valorBruto, funrural, chapa, icms, frete, custoMaq, custoSac,
      pesoPagavel, tonLiq, fretePorTon,
      // A pagar ao PRODUTOR: apenas Funrural é descontado.
      liquido: valorBruto - funrural,
      // Custos operacionais da EMPRESA (não descontam do produtor):
      custosEmpresa: chapa + icms + frete + custoMaq + custoSac,
      precoMedioCaixa: totalCaixas > 0 ? valorBruto / totalCaixas : 0,
      precoMedioKg: pesoLiquido > 0 ? valorBruto / pesoLiquido : 0,
    };
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = {
        modo_precificacao: form.modo_precificacao,
        preco_balancao: Number(form.preco_balancao) || 0,
        desconto_balancao_pct: Number(form.desconto_balancao_pct) || 0,
        cx1: Number(form.cx1) || 0,
        cx2: Number(form.cx2) || 0,
        cx3: Number(form.cx3) || 0,
        cx3g: Number(form.cx3g) || 0,
        cx4: Number(form.cx4) || 0,
        chapa: Number(form.chapa) || 0,
        icms_seguro: Number(form.icms_seguro) || 0,
        frete: Number(form.frete) || 0,
        fun_rural_pct: Number(form.fun_rural_pct) || 0,
        custo_maquinacao: Number(form.custo_maquinacao) || 0,
        sacaria: Number(form.sacaria) || 0,
      };
      const { data } = await api.put(`/invoices/${nf.id}/precos-custos`, payload);
      setPreview(data);
      await onSaved();
    } catch (e2) { setErr(formatErr(e2)); }
    finally { setBusy(false); }
  };

  const c = calc();
  const isBalancao = form.modo_precificacao === "balancao";

  return (
    <Card className="p-5" data-testid="price-form">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="font-mono font-bold text-lg text-ink-900">NF #{nf.nota_numero}</div>
          <div className="text-sm text-ink-700">{nf.fornecedor} • {nf.tipo_cebola}</div>
          <div className="text-xs text-ink-500 font-mono mt-1">
            {fmtDate(nf.data_beneficiamento)} • {fmtNum(totalCaixas, 0)} caixas • {fmtKg(pesoLiquido)}
          </div>
        </div>
        <button
          onClick={() => nav(`/invoices/${nf.id}`)}
          className="text-sm text-brand-500 hover:underline flex items-center gap-1"
        >
          Ver detalhes <ChevronRight size={14} />
        </button>
      </div>

      {/* Info Beneficiamento — % por categoria + % quebra */}
      <div className="mb-5 p-4 rounded-lg bg-stone-50 border border-ink-200">
        <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-3 flex items-center gap-1.5">
          <BarChart3 size={12} /> Composição do beneficiamento
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 items-stretch">
          {catsInfo.map((cat) => (
            <div key={cat.k} className={`p-2 rounded-md ${cat.color} border`} title={`${cat.subtitle}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold">{cat.label}</span>
                <span className="text-xs font-mono opacity-70">{fmtNum(cat.quantidade, 0)}cx</span>
              </div>
              <div className="mt-1 font-mono font-bold text-lg leading-none">{cat.pct.toFixed(1)}%</div>
              <div className="w-full bg-white/50 rounded-full h-1 mt-1 overflow-hidden">
                <div className="bg-black/20 h-full rounded-full" style={{ width: `${Math.min(100, cat.pct)}%` }} />
              </div>
            </div>
          ))}
          <div className={`p-2 rounded-md border ${quebraPct > 15 ? "bg-red-100 text-red-800 border-red-300" : quebraPct > 8 ? "bg-orange-100 text-orange-800 border-orange-300" : "bg-emerald-100 text-emerald-800 border-emerald-300"}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold flex items-center gap-1"><Percent size={10} /> Quebra</span>
              <span className="text-xs font-mono opacity-70">{fmtKg(quebraKg)}</span>
            </div>
            <div className="mt-1 font-mono font-bold text-lg leading-none">{quebraPct.toFixed(1)}%</div>
            <div className="w-full bg-white/50 rounded-full h-1 mt-1 overflow-hidden">
              <div className="bg-black/20 h-full rounded-full" style={{ width: `${Math.min(100, quebraPct * 3)}%` }} />
            </div>
          </div>
        </div>
      </div>

      {err && <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md mb-4">{err}</div>}

      <form onSubmit={submit} className="space-y-5">
        {/* Toggle Modo de Precificação */}
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-2">Modo de Precificação</div>
          <div className="inline-flex rounded-lg border border-ink-200 bg-stone-50 p-1" data-testid="modo-precificacao">
            <button
              type="button"
              onClick={() => setMode("boca_maquina")}
              className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors flex items-center gap-2 ${isBalancao ? "text-ink-500 hover:text-ink-700" : "bg-white text-brand-500 shadow-sm"}`}
              data-testid="modo-boca-maquina"
            >
              <Package size={16} /> Boca da Máquina
            </button>
            <button
              type="button"
              onClick={() => setMode("balancao")}
              className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors flex items-center gap-2 ${isBalancao ? "bg-white text-brand-500 shadow-sm" : "text-ink-500 hover:text-ink-700"}`}
              data-testid="modo-balancao"
            >
              <Scale size={16} /> Balanção
            </button>
          </div>
          <div className="text-xs text-ink-500 mt-1.5">
            {isBalancao
              ? "Fornecedor recebe pelo peso bruto entregue. A quebra fica por conta da empresa."
              : "Fornecedor recebe pelas caixas produzidas após classificação. A quebra fica por conta do fornecedor."}
          </div>
        </div>

        {/* Preços */}
        {isBalancao ? (
          <div className="p-4 rounded-lg bg-sky-50 border border-sky-200">
            <div className="text-xs font-bold uppercase tracking-[0.1em] text-sky-700 mb-2 flex items-center gap-1.5">
              <Scale size={12} /> Preço no Balanção
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-1">R$ por kg</label>
                <input
                  type="number" step="0.01" min="0" value={form.preco_balancao} onChange={set("preco_balancao")}
                  className="w-full rounded-md bg-white border border-sky-300 px-3 py-2 text-lg font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
                  data-testid="preco-balancao"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-1">Desconto no peso (%)</label>
                <input
                  type="number" step="0.01" min="0" max="100" value={form.desconto_balancao_pct} onChange={set("desconto_balancao_pct")}
                  className="w-full rounded-md bg-white border border-sky-300 px-3 py-2 text-lg font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
                  data-testid="desconto-balancao"
                  placeholder="0"
                />
              </div>
              <div className="p-2 rounded bg-white border border-sky-200">
                <div className="text-xs text-ink-500">Peso pagável</div>
                <div className="font-mono font-semibold">{fmtKg(c.pesoPagavel)}</div>
                {Number(form.desconto_balancao_pct) > 0 && (
                  <div className="text-[10px] text-ink-500 mt-0.5">de {fmtKg(pesoLiquido)}</div>
                )}
              </div>
              <div className="p-2 rounded bg-white border border-sky-200">
                <div className="text-xs text-ink-500">Valor bruto</div>
                <div className="font-mono font-bold text-brand-500">{fmtBRL(c.valorBruto)}</div>
              </div>
            </div>
            {Number(form.desconto_balancao_pct) > 0 && (
              <div className="mt-3 text-xs text-sky-700 bg-sky-100 rounded px-2 py-1.5 flex items-center gap-1.5">
                <Percent size={12} /> Desconto de {form.desconto_balancao_pct}% aplicado sobre {fmtKg(pesoLiquido)} → paga-se {fmtKg(c.pesoPagavel)} (redução de {fmtKg(pesoLiquido - c.pesoPagavel)})
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-2">Preço por saco (R$)</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {CATS.map((cat) => {
                const q = Number(benef[cat.k]?.quantidade || 0);
                return (
                  <div key={cat.k} className={`p-2 rounded ${cat.color}`}>
                    <div className="font-bold text-sm">{cat.label}</div>
                    <div className="text-xs opacity-70">{fmtNum(q, 0)} cx</div>
                    <input
                      type="number" step="0.01" min="0"
                      value={form[cat.k]}
                      onChange={set(cat.k)}
                      disabled={q === 0}
                      className="mt-1 w-full rounded bg-white border border-ink-200 px-2 py-1 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50"
                      data-testid={`price-${cat.k}`}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Custos */}
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-2">Custos</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <FieldNum label="Maquinação (R$/saco)" value={form.custo_maquinacao} onChange={set("custo_maquinacao")} testid="custo-maquinacao" />
            <FieldNum label="Sacaria (R$/saco)" value={form.sacaria} onChange={set("sacaria")} testid="custo-sacaria" />
            <FieldNum label="Funrural (%)" value={form.fun_rural_pct} onChange={set("fun_rural_pct")} testid="custo-funrural" />
            <FieldNum label="Frete (R$/tonelada líquida)" value={form.frete} onChange={set("frete")} testid="custo-frete" />
            <FieldNum label="Chapa (R$)" value={form.chapa} onChange={set("chapa")} testid="custo-chapa" />
            <FieldNum label="ICMS/Seguro (R$)" value={form.icms_seguro} onChange={set("icms_seguro")} testid="custo-icms-seguro" />
          </div>
        </div>

        {/* Preview */}
        <div className="p-4 rounded-lg bg-stone-50 border border-ink-200">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-3">Cálculo (prévia — modo {isBalancao ? "Balanção" : "Boca da Máquina"})</div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Coluna 1: Pagamento ao Produtor */}
            <div className="p-3 rounded-lg bg-white border border-emerald-200">
              <div className="text-xs font-bold uppercase tracking-[0.1em] text-emerald-700 mb-2 flex items-center gap-1.5">
                <DollarSign size={12} /> Pagamento ao Produtor
              </div>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-ink-500">Valor bruto</span>
                  <span className="font-mono font-semibold">{fmtBRL(c.valorBruto)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-ink-500">Funrural ({form.fun_rural_pct}%)</span>
                  <span className="font-mono text-danger">-{fmtBRL(c.funrural)}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-emerald-100">
                  <span className="text-emerald-700 font-bold">Líquido a pagar</span>
                  <span className="font-mono font-bold text-emerald-700 text-base">{fmtBRL(c.liquido)}</span>
                </div>
              </div>
            </div>

            {/* Coluna 2: Custos Operacionais da Empresa */}
            <div className="p-3 rounded-lg bg-white border border-ink-200">
              <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-2 flex items-center gap-1.5">
                <Package size={12} /> Custos Operacionais (Empresa)
              </div>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-ink-500">Maquinação</span>
                  <span className="font-mono text-ink-700">{fmtBRL(c.custoMaq)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-ink-500">Sacaria</span>
                  <span className="font-mono text-ink-700">{fmtBRL(c.custoSac)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-ink-500">Frete</span>
                  <span className="font-mono text-ink-700">{fmtBRL(c.frete)}</span>
                </div>
                {c.fretePorTon > 0 && c.tonLiq > 0 && (
                  <div className="text-[10px] text-ink-500 pl-2 -mt-1">
                    {fmtBRL(c.fretePorTon)}/t × {c.tonLiq.toFixed(3)}t líquidas
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-ink-500">Chapa</span>
                  <span className="font-mono text-ink-700">{fmtBRL(c.chapa)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-ink-500">ICMS/Seguro</span>
                  <span className="font-mono text-ink-700">{fmtBRL(c.icms)}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-ink-100">
                  <span className="text-ink-700 font-bold">Total custos empresa</span>
                  <span className="font-mono font-bold text-ink-900">{fmtBRL(c.custosEmpresa)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-500">
            <div>Preço médio: <span className="font-mono">{fmtBRL(c.precoMedioCaixa)}/cx</span></div>
            <div>• <span className="font-mono">{fmtBRL(c.precoMedioKg)}/kg</span></div>
            {nf.vencimento_fornecedor && (
              <div>• Vencimento AP: <span className="font-mono">{nf.vencimento_fornecedor}</span> (50 dias)</div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="submit" disabled={busy} data-testid="save-precos-btn">
            {busy ? <Spinner size={16} /> : <><DollarSign size={16} /> Salvar Preços & Custos</>}
          </Button>
        </div>

        {preview && (
          <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2 rounded flex items-center gap-2">
            <CheckCircle2 size={16} /> Preços salvos. Conta a Pagar gerada com vencimento em {preview.vencimento_fornecedor}.
          </div>
        )}
      </form>
    </Card>
  );
}

function FieldNum({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-1">{label}</label>
      <input
        type="number" step="0.01" min="0" value={value} onChange={onChange}
        className="w-full rounded bg-white border border-ink-200 px-2 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
        data-testid={testid}
      />
    </div>
  );
}
